import type { IconName } from '@careflux/primitives';
import { MicrophoneHelp } from '@careflux/primitives/microphone';
import type { ReactNode } from 'react';

export interface HelpStep {
  title: string;
  description: ReactNode;
  meta: string;
  images?: HelpStepImage[];
}

export interface HelpStepImage {
  src: string;
  alt: string;
  width: number;
  height: number;
}

export interface TutorialVideo {
  title: string;
  description: string;
  embedUrl: string;
  watchUrl: string;
}

export interface FaqItem {
  id: string;
  question: string;
  answer: ReactNode;
  featured?: boolean;
}

export interface FaqGroup {
  id: string;
  title: string;
  icon: IconName;
  items: FaqItem[];
}

export const helpSteps: HelpStep[] = [
  {
    title: 'Notaðu Google Chrome',
    description: 'Skríban virkar best í Google Chrome. Opnaðu Chrome áður en þú byrjar uppsetningu.',
    meta: 'Uppsetning',
  },
  {
    title: 'Farðu inn á scribe.careflux.ai',
    description: (
      <>
        Sláðu inn{' '}
        <a
          href="https://scribe.careflux.ai"
          target="_blank"
          rel="noreferrer"
          className="font-medium text-primary underline-offset-4 hover:underline"
        >
          Scribe.Careflux.ai
        </a>{' '}
        í vafrann og opnaðu innskráningarsíðuna.
      </>
    ),
    meta: 'Uppsetning',
  },
  {
    title: 'Staðfestu að upphafssíðan birtist',
    description: 'Þá ætti að taka á móti þér upphafssíða Skríbunnar.',
    meta: 'Uppsetning',
    images: [
      {
        src: '/help-center/quickstart/image4.png',
        alt: 'Upphafssíða Skríbunnar fyrir innskráningu',
        width: 598,
        height: 601,
      },
    ],
  },
  {
    title: 'Skráðu þig inn með rafrænum skilríkjum',
    description:
      'Skráðu þig inn með rafrænum skilríkjum og fylgdu leiðbeiningunum á skjánum. Eftir fyrstu innskráningu birtist passkey-uppsetning.',
    meta: 'Uppsetning',
    images: [
      {
        src: '/help-center/quickstart/image8.png',
        alt: 'Passkey uppsetning eftir fyrstu innskráningu',
        width: 532,
        height: 840,
      },
    ],
  },
  {
    title: 'Veldu Halda áfram og settu PIN',
    description:
      'Veldu Halda áfram. Ferlið tengir passkey við Google reikning eða aðra passkey þjónustu og endar á 6 stafa PIN tölu sem þú notar síðar við innskráningu.',
    meta: 'Uppsetning',
  },
  {
    title: 'Leyfðu vafranum að nota hljóðnema',
    description:
      'Þegar upphafsskjár Skríbunnar birtist kemur oft leyfisgluggi í vafranum. Veldu Allow every visit eða allow on this site svo vafrinn loki ekki á hljóðnemann.',
    meta: 'Heimildir',
    images: [
      {
        src: '/help-center/quickstart/image16.png',
        alt: 'Upphafsskjár Skríbunnar eftir innskráningu',
        width: 1689,
        height: 911,
      },
      {
        src: '/help-center/quickstart/image9.png',
        alt: 'Vafraleyfi fyrir hljóðnema',
        width: 560,
        height: 341,
      },
    ],
  },
  {
    title: 'Opnaðu sniðmát',
    description: 'Veldu Sniðmát vinstra megin á skjánum til að undirbúa fyrsta nótusniðmátið.',
    meta: 'Sniðmát',
    images: [
      {
        src: '/help-center/quickstart/image13.png',
        alt: 'Sniðmátasíða Skríbunnar',
        width: 1400,
        height: 506,
      },
    ],
  },
  {
    title: 'Opnaðu Kerfissniðmát',
    description: 'Ýttu á fellilistann Kerfissniðmát til að sjá sniðmát sem hægt er að afrita.',
    meta: 'Sniðmát',
    images: [
      {
        src: '/help-center/quickstart/image14.png',
        alt: 'Kerfissniðmát fellilisti',
        width: 1027,
        height: 435,
      },
    ],
  },
  {
    title: 'Afritaðu sniðmát til þín',
    description:
      'Veldu þrjá punkta hjá Heilsugæslunóta - eitt vandamál - BETA og veldu Afrita til mín. Opnaðu síðan Þín sniðmát og veldu Nota í samtal.',
    meta: 'Sniðmát',
    images: [
      {
        src: '/help-center/quickstart/image2.png',
        alt: 'Þriggja punkta aðgerð fyrir kerfissniðmát',
        width: 975,
        height: 119,
      },
      {
        src: '/help-center/quickstart/image12.png',
        alt: 'Nota afritað sniðmát í samtal',
        width: 989,
        height: 221,
      },
    ],
  },
  {
    title: 'Veldu nýja lotu',
    description: 'Veldu Ný lota vinstra megin á skjánum þegar sniðmátið er tilbúið.',
    meta: 'Undirbúningur',
    images: [
      {
        src: '/help-center/quickstart/image1.png',
        alt: 'Ný lota skjár í Skríbunni',
        width: 1651,
        height: 897,
      },
    ],
  },
  {
    title: 'Veldu réttan hljóðnema',
    description:
      'Athugaðu að réttur hljóðnemi sé valinn neðst til vinstri. Veldu þann sem þú notar, til dæmis Jabra Speak 275 eða tengd heyrnatól.',
    meta: 'Hljóð',
    images: [
      {
        src: '/help-center/quickstart/image3.png',
        alt: 'Fellilisti til að velja hljóðnema',
        width: 739,
        height: 116,
      },
    ],
  },
  {
    title: 'Veldu sjúkling og staðfestu samþykki',
    description:
      'Skráðu kennitölu í reitinn Velja sjúkling. Grænt merki þýðir undirritað samþykki, gult merkir óljósa stöðu og rautt merkir hafnað samþykki. Hljóðbylgjan við míkrófóninn á að hreyfast þegar þú talar.',
    meta: 'Samþykki',
    images: [
      {
        src: '/help-center/quickstart/image6.png',
        alt: 'Sjúklingaval, samþykkisstaða og hljóðbylgja',
        width: 746,
        height: 98,
      },
    ],
  },
  {
    title: 'Veldu viðtal eða dikteringu og byrjaðu upptöku',
    description:
      'Veldu hvort þú sért að taka viðtal eða dikteringu og ýttu á bláa míkrófónhnappinn. Þegar upptaka er hafin verður tíminn rauður og byrjar að telja.',
    meta: 'Upptaka',
    images: [
      {
        src: '/help-center/quickstart/image15.png',
        alt: 'Val á viðtali eða dikteringu',
        width: 310,
        height: 42,
      },
      {
        src: '/help-center/quickstart/image7.png',
        alt: 'Upptaka í gangi með rauðum tíma',
        width: 843,
        height: 154,
      },
    ],
  },
  {
    title: 'Stöðvaðu upptöku',
    description: 'Til að hætta upptöku smellir þú á rauða hnappinn með pásumerkinu.',
    meta: 'Upptaka',
  },
  {
    title: 'Bíddu eftir vinnslu og myndaðu samantekt',
    description:
      'Þegar glugginn Upptaka í bið birtist neðst hægra megin getur þú smellt á Mynda samantekt. Vinstri dálkur sýnir talgreindan texta og hægri dálkur sýnir virkt sniðmát og klíníska samantekt.',
    meta: 'Samantekt',
    images: [
      {
        src: '/help-center/quickstart/image10.png',
        alt: 'Talgreindur texti og klínísk aðstoð eftir upptöku',
        width: 1387,
        height: 821,
      },
    ],
  },
  {
    title: 'Leiðréttu samantektina',
    description: 'Þú getur breytt samantektinni með því að tvísmella fyrir ofan eða neðan textann.',
    meta: 'Yfirferð',
    images: [
      {
        src: '/help-center/quickstart/image5.png',
        alt: 'Breytingar á texta í samantekt',
        width: 597,
        height: 359,
      },
    ],
  },
  {
    title: 'Skoðaðu hvaðan upplýsingarnar koma',
    description:
      'Ef þú smellir beint á texta sýnir Skríban hvaða hluti samtalsins var notaður til að mynda þann bút samantektar.',
    meta: 'Yfirferð',
    images: [
      {
        src: '/help-center/quickstart/image11.png',
        alt: 'Tenging milli samantektar og samtalsbúts',
        width: 1585,
        height: 341,
      },
    ],
  },
  {
    title: 'Vistaðu og afritaðu í sjúkraskrárkerfið þitt',
    description:
      'Þegar þú hefur lokið yfirferð velur þú Vista og síðan Afrita samantekt. Textinn er þá færður inn í viðkomandi sjúkraskrárkerfi.',
    meta: 'Frágangur',
  },
];

export const tutorialVideo: TutorialVideo = {
  title: 'Skríban - Stutt yfirferð',
  description: 'Stutt myndband sem sýnir grunnflæði Skríbunnar frá fyrstu skrefum yfir í tilbúna nótu.',
  embedUrl: 'https://www.youtube-nocookie.com/embed/VgQdH-0kcuo',
  watchUrl: 'https://youtu.be/VgQdH-0kcuo',
};

export const faqGroups: FaqGroup[] = [
  {
    id: 'privacy-security',
    title: 'Persónuvernd og öryggi',
    icon: 'ShieldCheck',
    items: [
      {
        id: 'skriban-secure',
        question: 'Er Skríban örugg?',
        answer:
          'Skríban notar örugga innskráningu, dulkóðun, aðgangsstýringu og reglulegt öryggiseftirlit til að vernda gögn gegn óheimilum aðgangi eða misnotkun.',
        featured: true,
      },
      {
        id: 'patient-consent-recording',
        question: 'Hvernig samþykkir sjúklingur upptöku?',
        answer:
          'Heilbrigðisstarfsmaður þarf að upplýsa sjúkling um upptökuna og fá samþykki áður en viðtal er tekið upp.',
      },
      {
        id: 'revoke-consent',
        question: 'Er hægt að afturkalla samþykki?',
        answer:
          'Já. Beiðnum um afturköllun eða önnur réttindi skráðs aðila skal beina til viðkomandi heilbrigðisþjónustuveitanda eða persónuverndarfulltrúa Careflux.',
      },
      {
        id: 'recording-retention',
        question: 'Hvað er upptaka geymd lengi?',
        answer:
          'Vistun og meðferð gagna fer fram samkvæmt leiðbeiningum viðkomandi heilbrigðisþjónustuveitanda og gildandi lögum.',
      },
      {
        id: 'dictation-without-consent',
        question: 'Má diktera án samþykkis?',
        answer:
          'Já, Skríban má nota til dikteringar án sjúklings. Ef skráð er án persónugreinanlegra upplýsinga ber notanda að sleppa slíkum atriðum í dikteringunni.',
      },
    ],
  },
  {
    id: 'workflow',
    title: 'Notkun og verklag',
    icon: 'ClipboardList',
    items: [
      {
        id: 'how-skriban-works',
        question: 'Hvernig virkar Skríban?',
        answer:
          'Skríban tekur upp samtal eða dikteringu, vinnur sjálfvirkt úr hljóðinu og býr til samantekt sem heilbrigðisstarfsmaður yfirfer og breytir eftir þörfum.',
      },
      {
        id: 'training',
        question: 'Hvernig er Skríban þjálfuð?',
        answer:
          'Skríban byggir á gervigreindarlíkönum sem greina innihald samtala. Notandi þarf alltaf að yfirfara niðurstöðuna þar sem líkön geta gert villur.',
      },
      {
        id: 'customize-skriban',
        question: 'Hvernig get ég sniðið Skríbuna að mér?',
        answer:
          'Þú getur valið sniðmát sem hentar þínu verklagi eða afritað sniðmát og lagað það að því hvernig þú vilt byggja upp nótur.',
      },
      {
        id: 'why-use-skriban',
        question: 'Af hverju er Skríban gagnleg?',
        answer:
          'Skríban minnkar handvirka skráningu með því að búa til drög að samantekt, svo heilbrigðisstarfsmaður geti einbeitt sér betur að viðtalinu.',
      },
      {
        id: 'choose-template',
        question: 'Hvernig vel ég eða afrita sniðmát?',
        answer:
          'Veldu viðeigandi sniðmát áður en upptaka hefst. Ef þú þarft að breyta uppsetningu getur þú afritað sniðmát og aðlagað það að þínu verklagi.',
      },
    ],
  },
  {
    id: 'troubleshooting',
    title: 'Vandamál og lausnir',
    icon: 'Wrench',
    items: [
      {
        id: 'missing-summary',
        question: 'Upptaka er búin en engin samantekt birtist. Hvað geri ég?',
        answer:
          'Athugaðu fyrst nettengingu og hvort upptakan hafi klárast. Ef samantekt birtist enn ekki skaltu leita til þjónustuaðila eða Careflux.',
      },
      {
        id: 'microphone-speaker-missing',
        question: 'Hljóðnemi eða hátalari finnst ekki. Hvað gæti verið að?',
        answer: (
          <>
            <p>Staðfestu að rétt tæki sé valið og að búnaðurinn sé tengdur við tölvuna eða tækið.</p>
            <MicrophoneHelp />
          </>
        ),
      },
      {
        id: 'login-help',
        question: 'Ég get ekki skráð mig inn. Hvert leita ég?',
        answer:
          'Athugaðu rafræn skilríki og aðgang hjá þínum heilbrigðisþjónustuveitanda. Ef vandinn heldur áfram skaltu hafa samband við þjónustuaðila eða Careflux.',
      },
    ],
  },
];
